import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useApi } from 'services/api';
import { extractErrorMessage } from 'utils/api';

const useSensorPackages = (sensor) => {
  const api = useApi();
  const queryClient = useQueryClient();

  const { data, error, isLoading } = useQuery({
    queryKey: ['packages'],
    queryFn: () => api.get('/packages'),
    gcTime: Infinity,
    staleTime: Infinity,
  });

  const sensorPackages = useQuery({
    queryKey: ['tenants', sensor.Tenant.id, 'smf', 'sensors', sensor.id, 'packages'],

    queryFn: () => api.get(`/tenants/${sensor.Tenant.id}/smf/sensors/${sensor.id}/packages`),
  });

  const addPackageMutation = useMutation({
    mutationFn: (packageItem) =>
      api.post(`/tenants/${sensor.Tenant.id}/smf/sensors/${sensor.id}/packages/${packageItem.id}`),
    onSuccess: (_, variables) => {
      queryClient.setQueryData(['tenants', sensor.Tenant.id, 'smf', 'sensors', sensor.id, 'packages'], (oldData) => {
        return { ...oldData, items: [...oldData.items, variables] };
      });
    },
  });

  const removePackageMutation = useMutation({
    mutationFn: (packageItem) =>
      api.delete(`/tenants/${sensor.Tenant.id}/smf/sensors/${sensor.id}/packages/${packageItem.id}`),
    onSuccess: (_, variables) => {
      queryClient.setQueryData(['tenants', sensor.Tenant.id, 'smf', 'sensors', sensor.id, 'packages'], (oldData) => {
        return { ...oldData, items: oldData.items.filter((item) => item.id !== variables.id) };
      });
    },
  });

  const anyError = error || sensorPackages.error || addPackageMutation.error || removePackageMutation.error;

  return {
    isLoading: isLoading || sensorPackages.isLoading,
    isMutating: addPackageMutation.isPending || removePackageMutation.isPending,
    error: anyError && extractErrorMessage(anyError),
    allPackages: data?.items,
    currentActiveSensorIds: sensorPackages?.data?.items?.map((p) => p.id),
    addPackageToSensor: addPackageMutation.mutate,
    deletePackageFromSensor: removePackageMutation.mutate,
  };
};

export default useSensorPackages;
